import React, { useState, useEffect } from "react";
import { Table, Button, Space, Tooltip, Skeleton } from "antd";
import { EditOutlined, PlusOutlined, DeleteOutlined, KeyOutlined } from "@ant-design/icons";
import { getCustomers, deleteCustomer } from "../../services/customerService";
import CreateCustomerModal from "../modals/CreateCustomerModal";
import UpdateCustomerModal from "../modals/UpdateCustomerModal";
import ConfirmDeleteModal from "../common/ConfirmDeleteModal";
import ChangePasswordModal from "../modals/ChangePasswordModal";
import EmptyState from "../common/EmptyState";
import { toast } from "react-toastify";
import { motion } from "framer-motion";

const CustomerList = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [isPasswordModalVisible, setIsPasswordModalVisible] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const userId = localStorage.getItem("userId");

  const fetchCustomers = async (page = 1, pageSize = 10) => {
    setLoading(true);
    try {
      const response = await getCustomers(userId, page - 1, pageSize);
      setCustomers(response.data.content);
      setPagination({
        current: page,
        pageSize: pageSize,
        total: response.data.totalElements,
      });
    } catch (error) {
      toast.error("Müşteriler yüklenemedi");
      console.error("Error fetching customers:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers(pagination.current, pagination.pageSize);
  }, []);

  const handleTableChange = (pagination) => {
    fetchCustomers(pagination.current, pagination.pageSize);
  };

  const handleCreateCustomer = () => {
    setIsCreateModalVisible(true);
  };

  const handleEditCustomer = (customer) => {
    setSelectedCustomer(customer);
    setIsEditModalVisible(true);
  };

  const handleDeleteCustomer = (customer) => {
    setSelectedCustomer(customer);
    setIsDeleteModalVisible(true);
  };

  const handleChangePassword = (customer) => {
    setSelectedCustomer(customer);
    setIsPasswordModalVisible(true);
  };

  const handleCreateSuccess = () => {
    setIsCreateModalVisible(false);
    fetchCustomers(pagination.current, pagination.pageSize);
    toast.success("Müşteri başarıyla oluşturuldu", { icon: "🏢" });
  };

  const handleEditSuccess = () => {
    setIsEditModalVisible(false);
    fetchCustomers(pagination.current, pagination.pageSize);
    toast.success("Müşteri başarıyla güncellendi", { icon: "✅" });
  };

  const handleDeleteConfirm = async () => {
    if (!selectedCustomer) return;

    setDeleteLoading(true);
    try {
      await deleteCustomer(selectedCustomer.id, userId);
      setIsDeleteModalVisible(false);
      setSelectedCustomer(null);
      fetchCustomers(pagination.current, pagination.pageSize);
      toast.success("Müşteri başarıyla silindi", { icon: "🗑️" });
    } catch (error) {
      console.error("Müşteri silerken hata meydana geldi:", error);
      toast.error("Müşteri silinirken hata oluştu");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handlePasswordSuccess = () => {
    setIsPasswordModalVisible(false);
    setSelectedCustomer(null);
    toast.success("Müşteri şifresi başarıyla güncellendi", { icon: "🔐" });
  };

  const columns = [
    {
      title: "Müşteri Adı",
      dataIndex: "companyName",
      key: "companyName",
      sorter: true,
      render: (text) => (
        <span style={{ fontWeight: 500 }}>{text}</span>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (text) => (
        <a href={`mailto:${text}`} style={{ color: '#1890ff' }}>{text}</a>
      ),
    },
    {
      title: "Telefon",
      dataIndex: "phone",
      key: "phone",
    },
    {
      title: "İlgili Kişi",
      dataIndex: "contactPerson",
      key: "contactPerson",
    },
    {
      title: "Eylemler",
      key: "actions",
      render: (_, record) => (
        <Space size="small" className="table-actions">
          <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
            <Tooltip title="Düzenle" color="black">
              <Button
                icon={<EditOutlined style={{ color: '#60504f' }} />}
                onClick={() => handleEditCustomer(record)}
                size="small"
              />
            </Tooltip>
          </motion.div>
          <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
            <Tooltip title="Şifre Değiştir" color="blue">
              <Button
                icon={<KeyOutlined style={{ color: '#1890ff' }} />}
                onClick={() => handleChangePassword(record)}
                size="small"
              />
            </Tooltip>
          </motion.div>
          <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
            <Tooltip title="Sil" color="red">
              <Button
                icon={<DeleteOutlined style={{ color: '#ff4d4f' }} />}
                onClick={() => handleDeleteCustomer(record)}
                size="small"
                danger
              />
            </Tooltip>
          </motion.div>
        </Space>
      ),
    },
  ];

  // Loading skeleton
  if (loading && customers.length === 0) {
    return (
      <div>
        <div style={{ marginBottom: 16, textAlign: "right" }}>
          <Skeleton.Button active style={{ width: 140 }} />
        </div>
        <Skeleton active paragraph={{ rows: 8 }} />
      </div>
    );
  }

  // Empty state
  if (!loading && customers.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div style={{ marginBottom: 16, textAlign: "right" }}>
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreateCustomer}
              className="btn-enhanced"
            >
              Müşteri Oluştur
            </Button>
          </motion.div>
        </div>
        <EmptyState
          type="customers"
          onAction={handleCreateCustomer}
        />
        <CreateCustomerModal
          visible={isCreateModalVisible}
          onCancel={() => setIsCreateModalVisible(false)}
          onSuccess={handleCreateSuccess}
        />
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <div style={{ marginBottom: 16, textAlign: "right" }}>
        <motion.div
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          style={{ display: 'inline-block' }}
        >
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreateCustomer}
            className="btn-enhanced"
          >
            Müşteri Oluştur
          </Button>
        </motion.div>
      </div>
      <Table
        columns={columns}
        dataSource={customers}
        rowKey="id"
        pagination={pagination}
        loading={loading}
        onChange={handleTableChange}
        rowClassName="table-row-animated"
        style={{ borderRadius: 8, overflow: 'hidden' }}
      />
      <CreateCustomerModal
        visible={isCreateModalVisible}
        onCancel={() => setIsCreateModalVisible(false)}
        onSuccess={handleCreateSuccess}
      />
      <UpdateCustomerModal
        visible={isEditModalVisible}
        customer={selectedCustomer}
        onCancel={() => setIsEditModalVisible(false)}
        onSuccess={handleEditSuccess}
      />
      <ConfirmDeleteModal
        visible={isDeleteModalVisible}
        onCancel={() => {
          setIsDeleteModalVisible(false);
          setSelectedCustomer(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Müşteriyi Sil"
        itemName={selectedCustomer?.companyName}
        loading={deleteLoading}
        warningText="Bu işlem geri alınamaz. Müşteri kalıcı olarak silinecektir."
      />
      <ChangePasswordModal
        visible={isPasswordModalVisible}
        onCancel={() => {
          setIsPasswordModalVisible(false);
          setSelectedCustomer(null);
        }}
        onSuccess={handlePasswordSuccess}
        entity={selectedCustomer}
        entityType="customer"
      />
    </motion.div>
  );
};

export default CustomerList;
